import { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { PickerItem, FlatItemData } from './types';

interface UseSilentPickerProps {
  items: PickerItem[];
  selectedKey?: string;
  onSelectionChange?: (
    key: string,
    item: PickerItem,
    sectionLabel?: string
  ) => void;
  isDisabled?: boolean;
}

export function useSilentPicker({
  items,
  selectedKey,
  onSelectionChange,
  isDisabled = false
}: UseSilentPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  // Flatten items to get all selectable items (type === 'item')
  const flatItems = useMemo(() => {
    const flattened: FlatItemData[] = [];

    items.forEach(item => {
      if (item.type === 'item') {
        flattened.push({ item, sectionLabel: undefined });
      } else if (item.type === 'header' && item.children) {
        item.children.forEach(child => {
          if (child.type === 'item') {
            flattened.push({ item: child, sectionLabel: item.label });
          }
        });
      }
    });

    return flattened;
  }, [items]);

  const selectedItemData = flatItems.find(
    ({ item }) => item.id === selectedKey
  );
  const selectedItem = selectedItemData?.item;

  useLayoutEffect(() => {
    if (isOpen && listboxRef.current && triggerRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = listboxRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (triggerRect.left + popoverRect.width > viewportWidth) {
        setPopoverStyle({ left: 'auto', right: '-20px' });
      } else {
        // setPopoverStyle({ left: 0, right: 'auto' });
      }

      // Scroll to selected item when dropdown opens
      if (selectedItem) {
        const selectedIndex = flatItems.findIndex(
          ({ item }) => item.id === selectedKey
        );
        if (selectedIndex >= 0) {
          setFocusedIndex(selectedIndex);

          const selectedElement = listboxRef.current?.querySelector(
            `[data-item-id="${selectedKey}"]`
          ) as HTMLElement;
          if (selectedElement && listboxRef.current) {
            // Scroll within the dropdown container instead of the entire page
            const container = listboxRef.current;

            // Calculate the scroll position to center the element
            const elementTop = selectedElement.offsetTop;
            const elementHeight = selectedElement.offsetHeight;
            const containerHeight = container.clientHeight;

            // Center the element within the container
            const scrollTop =
              elementTop - containerHeight / 2 + elementHeight / 2;

            container.scrollTop = Math.max(0, scrollTop);
          }
        }
      }
    }
  }, [isOpen, selectedKey, selectedItem, flatItems]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        listboxRef.current &&
        !listboxRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
      setFocusedIndex(-1);
    }
  };

  const handleSelect = (item: PickerItem, sectionLabel?: string) => {
    onSelectionChange?.(item.id, item, sectionLabel);
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (isOpen && focusedIndex >= 0) {
          const focusedItemData = flatItems[focusedIndex];
          handleSelect(focusedItemData.item, focusedItemData.sectionLabel);
        } else {
          handleToggle();
        }
        break;
      case 'Escape':
        if (isOpen) {
          event.preventDefault();
          setIsOpen(false);
          setFocusedIndex(-1);
          triggerRef.current?.focus();
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(0);
        } else {
          setFocusedIndex(prev => Math.min(prev + 1, flatItems.length - 1));
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(flatItems.length - 1);
        } else {
          setFocusedIndex(prev => Math.max(prev - 1, 0));
        }
        break;
      case 'Home':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(0);
        }
        break;
      case 'End':
        if (isOpen) {
          event.preventDefault();
          setFocusedIndex(flatItems.length - 1);
        }
        break;
    }
  };

  const handleItemKeyDown = (
    event: React.KeyboardEvent,
    item: PickerItem,
    sectionLabel?: string
  ) => {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleSelect(item, sectionLabel);
        break;
    }
  };

  return {
    isOpen,
    focusedIndex,
    popoverStyle,
    triggerRef,
    listboxRef,
    flatItems,
    selectedItem,
    handleToggle,
    handleSelect,
    handleKeyDown,
    handleItemKeyDown
  };
}
